import { useEffect, useState, type ReactNode } from 'react';
import type { ReportRow } from '@/core/models/reports.models';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import './ReportFullScreen.scss';

/** `<button mat-menu-item [title]><mat-icon fontSet="material-icons-outlined">..</mat-icon>..</button>`. */
export function ReportMenuItem({
  icon,
  outlined = true,
  title,
  onClick,
  children,
}: {
  icon: string;
  outlined?: boolean;
  title?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="mat-mdc-menu-item mat-mdc-focus-indicator"
      title={title}
      onClick={onClick}
    >
      <Icon name={icon} outlined={outlined} />
      <span className="mat-mdc-menu-item-text">{children}</span>
    </button>
  );
}

/** prod-p3: the trial balance behind this report was deleted, so the report can no longer be produced. */
export function CannotBeProduced({ row }: { row: ReportRow }) {
  if (row.canProduce !== false) {
    return null;
  }
  return (
    <div className="text-red font-size-12 d-flex align-items-center" title={row.blockedReason ?? undefined}>
      <Icon name="error_outline" outlined className="font-size-14" />
      &nbsp;Cannot be produced
    </div>
  );
}

/**
 * APT342: where a report's figures came from - the Source trial balance, or the Adjusted trial balance of an accounting period - and,
 * for an adjusted report, whether the adjusted balances have moved on since it was generated (a journal was posted or reversed).
 * A report from an API that does not send the source is a Source trial balance, as every report was before.
 */
export function ReportSource({ row }: { row: ReportRow }) {
  const adjusted = row.balanceSource === 1;
  return (
    <>
      <span className="white-space-nowrap">
        {row.sourceLabel || (adjusted ? 'Adjusted trial balance' : 'Source trial balance')}
      </span>
      {adjusted && row.sourceChanged && (
        <div
          className="text-red font-size-12 d-flex align-items-center"
          title={
            row.sourceChangedReason ||
            'Journals or the trial balance changed after this report was generated. Regenerate it to bring it up to date.'
          }
        >
          <Icon name="update" outlined className="font-size-14" />
          &nbsp;Adjusted balances changed
        </div>
      )}
    </>
  );
}

/**
 * Oct-10 #182-#189: as in the old app, a generated Trial Balance or Nominal Ledger report can be extended to fill the
 * screen; the icon (or Esc) brings it back. `className` goes on the report's container.
 */
export function useReportFullScreen() {
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    if (!fullScreen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullScreen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullScreen]);

  return { fullScreen, toggle: () => setFullScreen((on) => !on), className: fullScreen ? ' report-full-screen' : '' };
}

export function FullScreenButton({ fullScreen, onClick }: { fullScreen: boolean; onClick: () => void }) {
  const title = fullScreen ? 'Exit full screen' : 'Full screen';
  return (
    <Button title={title} aria-label={title} onClick={onClick}>
      <Icon name={fullScreen ? 'fullscreen_exit' : 'fullscreen'} />
    </Button>
  );
}
